import { useState } from 'react';
import { BarChart3, Shield, CheckCircle, XCircle, User, Fingerprint, Settings, MapPin, AlertTriangle } from 'lucide-react';
import { formatDateTime } from '@/utils/dateFormat';

/**
 * Map IANA timezones to ISO country codes.
 * Covers the most common timezones; unknown ones fall back to the region prefix.
 */
const TIMEZONE_TO_COUNTRY: Record<string, { code: string; name: string }> = {
  // Africa
  'Africa/Lagos': { code: 'NG', name: 'Nigeria' },
  'Africa/Accra': { code: 'GH', name: 'Ghana' },
  'Africa/Nairobi': { code: 'KE', name: 'Kenya' },
  'Africa/Johannesburg': { code: 'ZA', name: 'South Africa' },
  'Africa/Cairo': { code: 'EG', name: 'Egypt' },
  'Africa/Casablanca': { code: 'MA', name: 'Morocco' },
  'Africa/Dar_es_Salaam': { code: 'TZ', name: 'Tanzania' },
  'Africa/Addis_Ababa': { code: 'ET', name: 'Ethiopia' },
  'Africa/Kampala': { code: 'UG', name: 'Uganda' },
  'Africa/Khartoum': { code: 'SD', name: 'Sudan' },
  'Africa/Lusaka': { code: 'ZM', name: 'Zambia' },
  'Africa/Harare': { code: 'ZW', name: 'Zimbabwe' },
  'Africa/Douala': { code: 'CM', name: 'Cameroon' },
  'Africa/Abidjan': { code: 'CI', name: 'Ivory Coast' },
  'Africa/Dakar': { code: 'SN', name: 'Senegal' },
  // Americas
  'America/New_York': { code: 'US', name: 'United States (Eastern)' },
  'America/Chicago': { code: 'US', name: 'United States (Central)' },
  'America/Denver': { code: 'US', name: 'United States (Mountain)' },
  'America/Los_Angeles': { code: 'US', name: 'United States (Pacific)' },
  'America/Anchorage': { code: 'US', name: 'United States (Alaska)' },
  'Pacific/Honolulu': { code: 'US', name: 'United States (Hawaii)' },
  'America/Toronto': { code: 'CA', name: 'Canada (Eastern)' },
  'America/Vancouver': { code: 'CA', name: 'Canada (Pacific)' },
  'America/Winnipeg': { code: 'CA', name: 'Canada (Central)' },
  'America/Edmonton': { code: 'CA', name: 'Canada (Mountain)' },
  'America/Mexico_City': { code: 'MX', name: 'Mexico' },
  'America/Sao_Paulo': { code: 'BR', name: 'Brazil' },
  'America/Argentina/Buenos_Aires': { code: 'AR', name: 'Argentina' },
  'America/Bogota': { code: 'CO', name: 'Colombia' },
  'America/Lima': { code: 'PE', name: 'Peru' },
  'America/Santiago': { code: 'CL', name: 'Chile' },
  'America/Caracas': { code: 'VE', name: 'Venezuela' },
  // Europe
  'Europe/London': { code: 'GB', name: 'United Kingdom' },
  'Europe/Dublin': { code: 'IE', name: 'Ireland' },
  'Europe/Paris': { code: 'FR', name: 'France' },
  'Europe/Berlin': { code: 'DE', name: 'Germany' },
  'Europe/Madrid': { code: 'ES', name: 'Spain' },
  'Europe/Rome': { code: 'IT', name: 'Italy' },
  'Europe/Amsterdam': { code: 'NL', name: 'Netherlands' },
  'Europe/Brussels': { code: 'BE', name: 'Belgium' },
  'Europe/Zurich': { code: 'CH', name: 'Switzerland' },
  'Europe/Vienna': { code: 'AT', name: 'Austria' },
  'Europe/Stockholm': { code: 'SE', name: 'Sweden' },
  'Europe/Oslo': { code: 'NO', name: 'Norway' },
  'Europe/Copenhagen': { code: 'DK', name: 'Denmark' },
  'Europe/Helsinki': { code: 'FI', name: 'Finland' },
  'Europe/Warsaw': { code: 'PL', name: 'Poland' },
  'Europe/Prague': { code: 'CZ', name: 'Czech Republic' },
  'Europe/Budapest': { code: 'HU', name: 'Hungary' },
  'Europe/Bucharest': { code: 'RO', name: 'Romania' },
  'Europe/Athens': { code: 'GR', name: 'Greece' },
  'Europe/Istanbul': { code: 'TR', name: 'Turkey' },
  'Europe/Moscow': { code: 'RU', name: 'Russia' },
  'Europe/Lisbon': { code: 'PT', name: 'Portugal' },
  'Europe/Kiev': { code: 'UA', name: 'Ukraine' },
  'Europe/Kyiv': { code: 'UA', name: 'Ukraine' },
  // Asia
  'Asia/Tokyo': { code: 'JP', name: 'Japan' },
  'Asia/Shanghai': { code: 'CN', name: 'China' },
  'Asia/Hong_Kong': { code: 'HK', name: 'Hong Kong' },
  'Asia/Singapore': { code: 'SG', name: 'Singapore' },
  'Asia/Seoul': { code: 'KR', name: 'South Korea' },
  'Asia/Kolkata': { code: 'IN', name: 'India' },
  'Asia/Calcutta': { code: 'IN', name: 'India' },
  'Asia/Dubai': { code: 'AE', name: 'UAE' },
  'Asia/Riyadh': { code: 'SA', name: 'Saudi Arabia' },
  'Asia/Jakarta': { code: 'ID', name: 'Indonesia' },
  'Asia/Bangkok': { code: 'TH', name: 'Thailand' },
  'Asia/Manila': { code: 'PH', name: 'Philippines' },
  'Asia/Karachi': { code: 'PK', name: 'Pakistan' },
  'Asia/Dhaka': { code: 'BD', name: 'Bangladesh' },
  'Asia/Kuala_Lumpur': { code: 'MY', name: 'Malaysia' },
  'Asia/Ho_Chi_Minh': { code: 'VN', name: 'Vietnam' },
  'Asia/Taipei': { code: 'TW', name: 'Taiwan' },
  'Asia/Jerusalem': { code: 'IL', name: 'Israel' },
  'Asia/Tehran': { code: 'IR', name: 'Iran' },
  // Oceania
  'Australia/Sydney': { code: 'AU', name: 'Australia (Eastern)' },
  'Australia/Melbourne': { code: 'AU', name: 'Australia (Eastern)' },
  'Australia/Perth': { code: 'AU', name: 'Australia (Western)' },
  'Australia/Brisbane': { code: 'AU', name: 'Australia (Queensland)' },
  'Pacific/Auckland': { code: 'NZ', name: 'New Zealand' },
};

/**
 * Infer a likely country from browser fingerprint signals.
 * Returns the inferred location and confidence reasoning.
 */
function inferLocation(fingerprint: any): {
  country: string | null;
  countryCode: string | null;
  confidence: 'high' | 'medium' | 'low';
  signals: string[];
} | null {
  if (!fingerprint) return null;

  const signals: string[] = [];
  let tzCountry: { code: string; name: string } | null = null;
  let langCountryCode: string | null = null;

  // 1. Timezone → country (strongest signal)
  if (fingerprint.tz && TIMEZONE_TO_COUNTRY[fingerprint.tz]) {
    tzCountry = TIMEZONE_TO_COUNTRY[fingerprint.tz];
    signals.push(`Timezone ${fingerprint.tz} → ${tzCountry.name}`);
  }

  // 2. Browser language → country hint
  const primaryLang: string | null = Array.isArray(fingerprint.langs) && fingerprint.langs.length > 0
    ? fingerprint.langs[0]
    : null;
  if (primaryLang) {
    // Extract region from locale (e.g., "en-US" → "US", "en-GB" → "GB", "pt-BR" → "BR")
    const match = primaryLang.match(/^[a-z]{2,3}-([A-Z]{2})$/i);
    if (match) {
      langCountryCode = match[1].toUpperCase();
      signals.push(`Primary language ${primaryLang} → ${langCountryCode}`);
    }
  }

  // 3. Accept-Language header
  const acceptLang = fingerprint.server?.acceptLanguage;
  if (acceptLang) {
    const headerMatch = acceptLang.match(/^[a-z]{2,3}-([A-Z]{2})/i);
    if (headerMatch) {
      const headerCountry = headerMatch[1].toUpperCase();
      if (!langCountryCode) {
        langCountryCode = headerCountry;
      }
      signals.push(`Accept-Language header → ${headerCountry}`);
    }
  }

  if (!tzCountry && !langCountryCode) return null;

  // Determine confidence
  let confidence: 'high' | 'medium' | 'low' = 'low';
  if (tzCountry && langCountryCode) {
    // Both signals agree
    if (tzCountry.code === langCountryCode) {
      confidence = 'high';
    } else {
      // Timezone and language disagree — still report timezone as primary
      confidence = 'medium';
      signals.push(`Note: timezone suggests ${tzCountry.code} but language suggests ${langCountryCode}`);
    }
  } else if (tzCountry) {
    confidence = 'medium';
  } else {
    confidence = 'low';
  }

  return {
    country: tzCountry?.name || langCountryCode,
    countryCode: tzCountry?.code || langCountryCode,
    confidence,
    signals,
  };
}

interface ResearchDefenderModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any;
  maskedId: string;
  blockReason?: string | null;
  blockType?: string | null;
  browserFingerprint?: any;
}

const ResearchDefenderModal: React.FC<ResearchDefenderModalProps> = ({
  isOpen,
  onClose,
  data,
  maskedId,
  blockReason,
  blockType,
  browserFingerprint
}) => {
  if (!isOpen) return null;

  // Infer location from browser fingerprint
  const inferredLocation = inferLocation(browserFingerprint);
  const rdCountryCode = data?.Respondent?.country_code?.toUpperCase() || null;
  const targetCountry = browserFingerprint?.config?.countryCodeSent?.toUpperCase() || null;
  const locationMismatch = inferredLocation?.countryCode && targetCountry
    ? inferredLocation.countryCode !== targetCountry
    : false;

  const renderKeyValueTable = (obj: any): JSX.Element[] => {
    const elements: JSX.Element[] = [];

    if (obj === null || obj === undefined) {
      return [<tr key="null"><td className="py-2 px-4 text-gray-500 italic">No data available</td></tr>];
    }

    Object.entries(obj).forEach(([key, value]) => {
      const displayKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const displayValue = formatValue(value);
      
      elements.push(
        <tr key={key} className="border-b border-gray-100">
          <td className="py-2 px-4 font-mono text-sm text-gray-700 font-medium">{displayKey}</td>
          <td className="py-2 px-4">
            {typeof value === 'boolean' ? (
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {value ? 'Yes' : 'No'}
              </span>
            ) : typeof value === 'object' && value !== null ? (
              <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded border">
                {JSON.stringify(value, null, 2)}
              </div>
            ) : (
              <span className="font-mono text-sm">{displayValue}</span>
            )}
          </td>
        </tr>
      );
    });

    return elements;
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value ? 'True' : 'False';
    if (Array.isArray(value)) return value.length > 0 ? `[${value.length} items]` : 'Empty array';
    return JSON.stringify(value);
  };

  // Helper to display values, showing actual data instead of "N/A" for 0 or empty string
  const displayValue = (value: any, fallback = 'N/A'): string => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'string' && value.trim() === '') return fallback;
    return value.toString();
  };

  // Format block type for display
  const formatBlockType = (type: string | null): string => {
    if (!type) return '';
    const typeMap: Record<string, string> = {
      country_mismatch: 'Country Mismatch',
      duplicate: 'Duplicate Detected',
      threat_score: 'High Threat Score',
      failure_reason: 'RD Failure'
    };
    return typeMap[type] || type.replace(/_/g, ' ');
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const extractScore = () => {
    if (data?.data?.respondent?.score) {
      return data.data.respondent.score;
    }
    return null;
  };

  const extractFlags = () => {
    if (data?.data?.respondent?.flags) {
      return Array.isArray(data.data.respondent.flags) ? data.data.respondent.flags : [];
    }
    return [];
  };

  const score = extractScore();
  const flags = extractFlags();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-hidden">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl max-h-[90vh] w-full mx-4 overflow-y-auto overscroll-contain">
        {/* Header */}
        <div className="bg-blue-600 text-white p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold mb-2 flex items-center gap-2"><Shield className="w-6 h-6" /> ResearchDefender Data</h2>
              <p className="text-blue-100">Respondent ID: {maskedId}</p>
            </div>
            <button
              onClick={onClose}
              className="text-blue-200 hover:text-white transition-colors text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Blocking Reason Banner */}
        {blockReason && blockType && (
          <div className="bg-red-50 border-b border-red-200 p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-red-800">
                  Blocked: {formatBlockType(blockType)}
                </p>
                <p className="text-sm text-red-700 mt-1">
                  {blockReason}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* RC Location Inference Banner */}
        {inferredLocation && (
          <div className={`border-b p-4 ${locationMismatch ? 'bg-orange-50 border-orange-200' : 'bg-emerald-50 border-emerald-200'}`}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {locationMismatch
                  ? <AlertTriangle className="w-5 h-5 text-orange-600" />
                  : <MapPin className="w-5 h-5 text-emerald-600" />}
              </div>
              <div className="flex-1">
                <p className={`text-sm font-semibold ${locationMismatch ? 'text-orange-800' : 'text-emerald-800'}`}>
                  {locationMismatch ? 'Location Contradiction Detected' : 'RC Location Estimate'}
                </p>
                <p className={`text-sm mt-1 ${locationMismatch ? 'text-orange-700' : 'text-emerald-700'}`}>
                  Browser signals suggest: <strong>{inferredLocation.country} ({inferredLocation.countryCode})</strong>
                  {targetCountry && (
                    <span> — Target country: <strong>{targetCountry}</strong></span>
                  )}
                  {rdCountryCode && rdCountryCode !== inferredLocation.countryCode && (
                    <span> — RD detected: <strong>{rdCountryCode}</strong></span>
                  )}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    inferredLocation.confidence === 'high' ? 'bg-green-100 text-green-800' :
                    inferredLocation.confidence === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {inferredLocation.confidence} confidence
                  </span>
                  {locationMismatch && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                      Does not match target country
                    </span>
                  )}
                </div>
                <ul className="mt-2 text-xs text-gray-600 space-y-0.5">
                  {inferredLocation.signals.map((signal, i) => (
                    <li key={i}>- {signal}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Quick Summary */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Status</p>
              <p className={`text-lg font-semibold ${data?.success ? 'text-green-600' : 'text-red-600'}`}>
                {data?.success ? <span className="flex items-center gap-1"><CheckCircle className="w-5 h-5" /> Success</span> : <span className="flex items-center gap-1"><XCircle className="w-5 h-5" /> Failed</span>}
              </p>
            </div>
            
            {score !== null && (
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-1">Risk Score</p>
                <p className={`text-lg font-semibold px-3 py-1 rounded ${getScoreColor(score)}`}>
                  {score}/100
                </p>
              </div>
            )}
            
            {flags.length > 0 && (
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-1">Flags</p>
                <div className="flex flex-wrap gap-1 justify-center">
                  {flags.map((flag: string, index: number) => (
                    <span 
                      key={index} 
                      className="text-xs px-2 py-1 bg-orange-100 text-orange-800 rounded-full"
                    >
                      {flag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ResearchDefender Data Tables */}
        <div className="p-6 space-y-6">
          {/* Table 1: Surveys */}
          {data?.Surveys && Array.isArray(data.Surveys) && data.Surveys.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3 text-gray-800 flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Surveys
              </h3>
              <div className="border border-gray-200 rounded-lg overflow-auto">
                <table className="w-full min-w-max">
                  <tbody>
                    <tr className="bg-blue-50">
                      <td className="py-2 px-4 font-semibold text-blue-700 border-r border-blue-200 w-32">sn_ud</td>
                      <td className="py-2 px-4 font-mono text-sm">{data.Surveys[0]?.sn_ud || 'N/A'}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-blue-700 border-r border-blue-200">duplicate_potential</td>
                      <td className="py-2 px-4 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          data.Surveys[0]?.duplicate_potential === 'high' 
                            ? 'bg-red-100 text-red-800' 
                            : data.Surveys[0]?.duplicate_potential === 'medium'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {data.Surveys[0]?.duplicate_potential?.toUpperCase() || 'N/A'}
                        </span>
                      </td>
                    </tr>
                    <tr className="bg-blue-50">
                      <td className="py-2 px-4 font-semibold text-blue-700 border-r border-blue-200">failure_reason</td>
                      <td className="py-2 px-4 text-sm font-mono">{displayValue(data.Surveys[0]?.failure_reason)}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-blue-700 border-r border-blue-200">duplicate_score</td>
                      <td className="py-2 px-4 text-sm font-mono">{displayValue(data.Surveys[0]?.duplicate_score)}</td>
                    </tr>
                    <tr className="bg-blue-50">
                      <td className="py-2 px-4 font-semibold text-blue-700 border-r border-blue-200">flag</td>
                      <td className="py-2 px-4 text-sm">{displayValue(data.Surveys[0]?.flag)}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-blue-700 border-r border-blue-200">country_mismatch</td>
                      <td className="py-2 px-4 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          data.Surveys[0]?.country_mismatch === 1 || data.Surveys[0]?.country_mismatch === '1' || data.Surveys[0]?.country_mismatch === true
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {displayValue(data.Surveys[0]?.country_mismatch) === '1' ? 'Yes (Mismatch)' : 'No'}
                        </span>
                      </td>
                    </tr>
                    <tr className="bg-blue-50">
                      <td className="py-2 px-4 font-semibold text-blue-700 border-r border-blue-200">survey_number</td>
                      <td className="py-2 px-4 text-sm font-mono">{data.Surveys[0]?.survey_number || 'N/A'}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-blue-700 border-r border-blue-200">duplicate_initial_ud</td>
                      <td className="py-2 px-4 text-sm font-mono">{displayValue(data.Surveys[0]?.duplicate_initial_ud)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Table 2: Respondent */}
          {data?.Respondent && (
            <div>
              <h3 className="text-lg font-semibold mb-3 text-gray-800 flex items-center gap-2">
                <User className="w-5 h-5" /> Respondent
              </h3>
              <div className="border border-gray-200 rounded-lg overflow-auto">
                <table className="w-full min-w-max">
                  <tbody>
                    <tr className="bg-green-50">
                      <td className="py-2 px-4 font-semibold text-green-700 border-r border-green-200 w-32">country</td>
                      <td className="py-2 px-4 text-sm">{data.Respondent.country || 'N/A'}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-green-700 border-r border-green-200">country_code</td>
                      <td className="py-2 px-4 text-sm font-mono">{data.Respondent.country_code || 'N/A'}</td>
                    </tr>
                    <tr className="bg-green-50">
                      <td className="py-2 px-4 font-semibold text-green-700 border-r border-green-200">respondent_risk</td>
                      <td className="py-2 px-4 text-sm font-mono">{displayValue(data.Respondent.respondent_risk)}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-green-700 border-r border-green-200">respondent_ud</td>
                      <td className="py-2 px-4 text-sm font-mono text-xs">{data.Respondent.respondent_ud || 'N/A'}</td>
                    </tr>
                    <tr className="bg-green-50">
                      <td className="py-2 px-4 font-semibold text-green-700 border-r border-green-200">threat_potential</td>
                      <td className="py-2 px-4 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          data.Respondent.threat_potential === 'high'
                            ? 'bg-red-100 text-red-800'
                            : data.Respondent.threat_potential === 'medium'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {data.Respondent.threat_potential?.toUpperCase() || 'N/A'}
                        </span>
                      </td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-green-700 border-r border-green-200">threat_potential_score</td>
                      <td className="py-2 px-4 text-sm font-mono">{data.Respondent.threat_potential_score || 'N/A'}</td>
                    </tr>
                    <tr className="bg-green-50">
                      <td className="py-2 px-4 font-semibold text-green-700 border-r border-green-200">device_type</td>
                      <td className="py-2 px-4 text-sm">{data.Respondent.device_type || 'N/A'}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-green-700 border-r border-green-200">browser_type</td>
                      <td className="py-2 px-4 text-sm">{data.Respondent.browser_type || 'N/A'}</td>
                    </tr>
                    <tr className="bg-green-50">
                      <td className="py-2 px-4 font-semibold text-green-700 border-r border-green-200">browser_full_name</td>
                      <td className="py-2 px-4 text-sm">{data.Respondent.browser_full_name || 'N/A'}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-green-700 border-r border-green-200">os_name</td>
                      <td className="py-2 px-4 text-sm">{data.Respondent.os_name || 'N/A'}</td>
                    </tr>
                    <tr className="bg-green-50">
                      <td className="py-2 px-4 font-semibold text-green-700 border-r border-green-200">ip_address</td>
                      <td className="py-2 px-4 text-sm font-mono">{data.Respondent.ip_address || 'N/A'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Table 3: RC Browser Signals */}
          {browserFingerprint && (browserFingerprint.tz || browserFingerprint.langs || browserFingerprint.server) && (
            <div>
              <h3 className="text-lg font-semibold mb-3 text-gray-800 flex items-center gap-2">
                <Fingerprint className="w-5 h-5" /> RC Browser Signals
              </h3>
              <p className="text-xs text-gray-500 mb-2">Collected from the respondent's browser at entry. Use to verify location claims against RD's IP-based detection.</p>
              <div className="border border-purple-200 rounded-lg overflow-auto">
                <table className="w-full min-w-max">
                  <tbody>
                    <tr className="bg-purple-50">
                      <td className="py-2 px-4 font-semibold text-purple-700 border-r border-purple-200 w-48">Browser Timezone</td>
                      <td className="py-2 px-4 text-sm font-mono">{browserFingerprint.tz || 'N/A'}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-purple-700 border-r border-purple-200">Browser Languages</td>
                      <td className="py-2 px-4 text-sm font-mono">
                        {Array.isArray(browserFingerprint.langs) ? browserFingerprint.langs.join(', ') : 'N/A'}
                      </td>
                    </tr>
                    <tr className="bg-purple-50">
                      <td className="py-2 px-4 font-semibold text-purple-700 border-r border-purple-200">Timezone Offset (min)</td>
                      <td className="py-2 px-4 text-sm font-mono">{browserFingerprint.tzOffset ?? 'N/A'}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-purple-700 border-r border-purple-200">Accept-Language Header</td>
                      <td className="py-2 px-4 text-sm font-mono">{browserFingerprint.server?.acceptLanguage || 'N/A'}</td>
                    </tr>
                    <tr className="bg-purple-50">
                      <td className="py-2 px-4 font-semibold text-purple-700 border-r border-purple-200">Screen Resolution</td>
                      <td className="py-2 px-4 text-sm font-mono">{browserFingerprint.screen || 'N/A'}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-purple-700 border-r border-purple-200">Device Pixel Ratio</td>
                      <td className="py-2 px-4 text-sm font-mono">{browserFingerprint.devicePixelRatio ?? 'N/A'}</td>
                    </tr>
                    <tr className="bg-purple-50">
                      <td className="py-2 px-4 font-semibold text-purple-700 border-r border-purple-200">Platform</td>
                      <td className="py-2 px-4 text-sm font-mono">{browserFingerprint.platform || 'N/A'}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-purple-700 border-r border-purple-200">CPU Cores</td>
                      <td className="py-2 px-4 text-sm font-mono">{browserFingerprint.hardwareConcurrency ?? 'N/A'}</td>
                    </tr>
                    <tr className="bg-purple-50">
                      <td className="py-2 px-4 font-semibold text-purple-700 border-r border-purple-200">Device Memory (GB)</td>
                      <td className="py-2 px-4 text-sm font-mono">{browserFingerprint.deviceMemory ?? 'N/A'}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-purple-700 border-r border-purple-200">Touch Points</td>
                      <td className="py-2 px-4 text-sm font-mono">{browserFingerprint.maxTouchPoints ?? 'N/A'}</td>
                    </tr>
                    {browserFingerprint.connection && (
                    <tr className="bg-purple-50">
                      <td className="py-2 px-4 font-semibold text-purple-700 border-r border-purple-200">Connection</td>
                      <td className="py-2 px-4 text-sm font-mono">
                        {browserFingerprint.connection.type || 'N/A'}
                        {browserFingerprint.connection.downlink != null && ` / ${browserFingerprint.connection.downlink} Mbps`}
                        {browserFingerprint.connection.rtt != null && ` / ${browserFingerprint.connection.rtt}ms RTT`}
                      </td>
                    </tr>
                    )}
                    {browserFingerprint.webglRenderer && (
                    <tr className={browserFingerprint.connection ? 'bg-white' : 'bg-purple-50'}>
                      <td className="py-2 px-4 font-semibold text-purple-700 border-r border-purple-200">GPU Renderer</td>
                      <td className="py-2 px-4 text-sm font-mono text-xs">{browserFingerprint.webglRenderer}</td>
                    </tr>
                    )}
                    {browserFingerprint.webglVendor && (
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-purple-700 border-r border-purple-200">GPU Vendor</td>
                      <td className="py-2 px-4 text-sm font-mono">{browserFingerprint.webglVendor}</td>
                    </tr>
                    )}
                    {browserFingerprint.canvasHash && (
                    <tr className="bg-purple-50">
                      <td className="py-2 px-4 font-semibold text-purple-700 border-r border-purple-200">Canvas Hash</td>
                      <td className="py-2 px-4 text-sm font-mono">{browserFingerprint.canvasHash}</td>
                    </tr>
                    )}
                    <tr className={browserFingerprint.canvasHash ? 'bg-white' : 'bg-purple-50'}>
                      <td className="py-2 px-4 font-semibold text-purple-700 border-r border-purple-200">Client Timestamp</td>
                      <td className="py-2 px-4 text-sm font-mono">{browserFingerprint.ts || 'N/A'}</td>
                    </tr>
                    {browserFingerprint.tokenStatus && (
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-purple-700 border-r border-purple-200">GETTOKEN Status</td>
                      <td className="py-2 px-4 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          browserFingerprint.tokenStatus.success
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {browserFingerprint.tokenStatus.success ? 'Success' : 'Failed'}
                        </span>
                        {!browserFingerprint.tokenStatus.success && browserFingerprint.tokenStatus.reason && (
                          <span className="ml-2 text-xs text-gray-500">{browserFingerprint.tokenStatus.reason}</span>
                        )}
                      </td>
                    </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Table 4: RC Request Config (Evidence) */}
          {browserFingerprint?.config && (
            <div>
              <h3 className="text-lg font-semibold mb-3 text-gray-800 flex items-center gap-2">
                <Settings className="w-5 h-5" /> RC Request Config
              </h3>
              <p className="text-xs text-gray-500 mb-2">What Research Clever sent to ResearchDefender for this respondent. Stored as evidence.</p>
              <div className="border border-amber-200 rounded-lg overflow-auto">
                <table className="w-full min-w-max">
                  <tbody>
                    <tr className="bg-amber-50">
                      <td className="py-2 px-4 font-semibold text-amber-700 border-r border-amber-200 w-48">Country Code Sent</td>
                      <td className="py-2 px-4 text-sm font-mono font-bold">{browserFingerprint.config.countryCodeSent || 'N/A'}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-amber-700 border-r border-amber-200">Target Country (Setting)</td>
                      <td className="py-2 px-4 text-sm font-mono">{browserFingerprint.config.rdSettings?.targetCountry || 'N/A'}</td>
                    </tr>
                    <tr className="bg-amber-50">
                      <td className="py-2 px-4 font-semibold text-amber-700 border-r border-amber-200">Geo Blocker Enabled</td>
                      <td className="py-2 px-4 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          browserFingerprint.config.rdSettings?.enableGeoBlocker
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {browserFingerprint.config.rdSettings?.enableGeoBlocker ? 'Yes' : 'No'}
                        </span>
                      </td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-amber-700 border-r border-amber-200">RD Enabled</td>
                      <td className="py-2 px-4 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          browserFingerprint.config.rdSettings?.rdEnabled
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {browserFingerprint.config.rdSettings?.rdEnabled ? 'Yes' : 'No'}
                        </span>
                      </td>
                    </tr>
                    <tr className="bg-amber-50">
                      <td className="py-2 px-4 font-semibold text-amber-700 border-r border-amber-200">RD Trigger Score</td>
                      <td className="py-2 px-4 text-sm font-mono">{browserFingerprint.config.rdSettings?.rdTriggerScore ?? 'N/A'}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-amber-700 border-r border-amber-200">Block Duplicates</td>
                      <td className="py-2 px-4 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          browserFingerprint.config.rdSettings?.rdBlockDuplicates
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {browserFingerprint.config.rdSettings?.rdBlockDuplicates ? 'Yes' : 'No'}
                        </span>
                      </td>
                    </tr>
                    <tr className="bg-amber-50">
                      <td className="py-2 px-4 font-semibold text-amber-700 border-r border-amber-200">Collection</td>
                      <td className="py-2 px-4 text-sm">{browserFingerprint.config.collectionName || 'N/A'}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-amber-700 border-r border-amber-200">Supplier</td>
                      <td className="py-2 px-4 text-sm">{browserFingerprint.config.supplierName || 'N/A'}</td>
                    </tr>
                    <tr className="bg-amber-50">
                      <td className="py-2 px-4 font-semibold text-amber-700 border-r border-amber-200">Had Token (V3)</td>
                      <td className="py-2 px-4 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          browserFingerprint.config.hadToken
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {browserFingerprint.config.hadToken ? 'Yes' : 'No (Legacy V4)'}
                        </span>
                      </td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-amber-700 border-r border-amber-200">Had Pass Params</td>
                      <td className="py-2 px-4 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          browserFingerprint.config.hadPassParams
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {browserFingerprint.config.hadPassParams ? 'Yes' : 'No'}
                        </span>
                      </td>
                    </tr>
                    <tr className="bg-amber-50">
                      <td className="py-2 px-4 font-semibold text-amber-700 border-r border-amber-200">Processed At</td>
                      <td className="py-2 px-4 text-sm font-mono">{browserFingerprint.config.timestamp ? formatDateTime(browserFingerprint.config.timestamp) : 'N/A'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Table 5: RD API Log */}
          {browserFingerprint?.rdApiLog && (
            <div>
              <h3 className="text-lg font-semibold mb-3 text-gray-800 flex items-center gap-2">
                <BarChart3 className="w-5 h-5" /> RD API Log
              </h3>
              <p className="text-xs text-gray-500 mb-2">Exact parameters sent to ResearchDefender and key fields from their response.</p>
              <div className="border border-cyan-200 rounded-lg overflow-auto">
                <table className="w-full min-w-max">
                  <tbody>
                    <tr className="bg-cyan-50">
                      <td colSpan={2} className="py-2 px-4 font-semibold text-cyan-800 text-xs uppercase tracking-wide">Request</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-cyan-700 border-r border-cyan-200 w-48">API Version</td>
                      <td className="py-2 px-4 text-sm font-mono">{browserFingerprint.rdApiLog.request?.version || 'N/A'}</td>
                    </tr>
                    <tr className="bg-cyan-50">
                      <td className="py-2 px-4 font-semibold text-cyan-700 border-r border-cyan-200">sy_nr (Project ID)</td>
                      <td className="py-2 px-4 text-sm font-mono">{browserFingerprint.rdApiLog.request?.params?.sy_nr || 'N/A'}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-cyan-700 border-r border-cyan-200">sn_ud (Respondent)</td>
                      <td className="py-2 px-4 text-sm font-mono text-xs">{browserFingerprint.rdApiLog.request?.params?.sn_ud || 'N/A'}</td>
                    </tr>
                    <tr className="bg-cyan-50">
                      <td className="py-2 px-4 font-semibold text-cyan-700 border-r border-cyan-200">rt_cy_ce (Country)</td>
                      <td className="py-2 px-4 text-sm font-mono font-bold">{browserFingerprint.rdApiLog.request?.params?.rt_cy_ce || 'NOT SENT'}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-cyan-700 border-r border-cyan-200">pass_params</td>
                      <td className="py-2 px-4 text-sm font-mono">{browserFingerprint.rdApiLog.request?.params?.pass_params || 'Not sent'}</td>
                    </tr>
                    <tr className="bg-cyan-50">
                      <td colSpan={2} className="py-2 px-4 font-semibold text-cyan-800 text-xs uppercase tracking-wide">Response</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-cyan-700 border-r border-cyan-200">API Success</td>
                      <td className="py-2 px-4 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          browserFingerprint.rdApiLog.response?.success
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {browserFingerprint.rdApiLog.response?.success ? 'Yes' : 'No'}
                        </span>
                        {browserFingerprint.rdApiLog.response?.error && (
                          <span className="ml-2 text-xs text-red-500">{browserFingerprint.rdApiLog.response.error}</span>
                        )}
                      </td>
                    </tr>
                    {browserFingerprint.rdApiLog.response?.respondent && (
                    <>
                    <tr className="bg-cyan-50">
                      <td className="py-2 px-4 font-semibold text-cyan-700 border-r border-cyan-200">Detected Country</td>
                      <td className="py-2 px-4 text-sm font-mono">
                        {browserFingerprint.rdApiLog.response.respondent.country || 'N/A'} ({browserFingerprint.rdApiLog.response.respondent.country_code || 'N/A'})
                      </td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-cyan-700 border-r border-cyan-200">Threat Score</td>
                      <td className="py-2 px-4 text-sm font-mono">
                        {browserFingerprint.rdApiLog.response.respondent.threat_potential_score ?? 'N/A'} ({browserFingerprint.rdApiLog.response.respondent.threat_potential || 'N/A'})
                      </td>
                    </tr>
                    </>
                    )}
                    {browserFingerprint.rdApiLog.response?.survey && (
                    <>
                    <tr className="bg-cyan-50">
                      <td className="py-2 px-4 font-semibold text-cyan-700 border-r border-cyan-200">RD country_mismatch</td>
                      <td className="py-2 px-4 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          browserFingerprint.rdApiLog.response.survey.country_mismatch === 1 || browserFingerprint.rdApiLog.response.survey.country_mismatch === true
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {browserFingerprint.rdApiLog.response.survey.country_mismatch === 1 || browserFingerprint.rdApiLog.response.survey.country_mismatch === true ? 'Yes' : 'No'}
                        </span>
                        {!(browserFingerprint.rdApiLog.response.survey.country_mismatch === 1 || browserFingerprint.rdApiLog.response.survey.country_mismatch === true) &&
                          browserFingerprint.rdApiLog.response.respondent?.country_code &&
                          browserFingerprint.config?.countryCodeSent &&
                          browserFingerprint.rdApiLog.response.respondent.country_code !== browserFingerprint.config.countryCodeSent && (
                          <span className="ml-2 text-xs text-red-500">
                            RD said No despite {browserFingerprint.rdApiLog.response.respondent.country_code} != {browserFingerprint.config.countryCodeSent}
                          </span>
                        )}
                      </td>
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-4 font-semibold text-cyan-700 border-r border-cyan-200">RD failure_reason</td>
                      <td className="py-2 px-4 text-sm font-mono">{browserFingerprint.rdApiLog.response.survey.failure_reason || 'None'}</td>
                    </tr>
                    </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-100 px-6 py-4 flex justify-end sticky bottom-0">
          <button
            onClick={onClose}
            className="btn btn-primary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResearchDefenderModal;